import { createApi } from '@reduxjs/toolkit/query/react';
import { PaylinkResponse } from 'skipify-types';

import {
  UpdatePaylinkBody,
  CreatePaylinkBody,
  GetAllPaylinksQuery,
  PaginatedResponse,
  SendPaylinkBody,
  DownloadPaylinksBody,
} from './types';
import { ATTACHMENT_VARS } from '../../../pages/Paylinks/constants';
import { fetchBaseQueryWithAuth } from '../../fetchBaseQuery';
import { runtimeConfig } from '../../../config/runtimeConfig';

export const invalidatePaylinkQueries = (paylinkId: string) =>
  [
    { type: 'Paylink', id: 'RECENTLY_SENT' },
    { type: 'Paylink', id: 'ALL' },
    { type: 'Paylink', id: paylinkId },
  ] as const;

export const paylinkApi = createApi({
  reducerPath: 'paylinkApi',
  tagTypes: ['Paylink'],
  baseQuery: fetchBaseQueryWithAuth(runtimeConfig.paylinkApiBaseUrl || ''),
  endpoints: (builder) => ({
    getPaylink: builder.query<PaylinkResponse, { paylinkId: string }>({
      query: ({ paylinkId }) => `/${paylinkId}?transactionStatus=all`,
      providesTags: (_result, _error, { paylinkId }) => [{ type: 'Paylink', id: paylinkId }],
    }),
    createPaylink: builder.mutation<PaylinkResponse, CreatePaylinkBody>({
      query: (body) => ({
        url: '/',
        method: 'POST',
        body,
      }),
    }),
    sendPaylink: builder.mutation<PaylinkResponse, SendPaylinkBody & { paylinkId: string }>({
      query: ({ paylinkId, ...body }) => ({
        url: `/${paylinkId}/send`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: [{ type: 'Paylink', id: 'RECENTLY_SENT' }],
    }),
    cancelPaylink: builder.mutation<PaylinkResponse, { paylinkId: string }>({
      query: ({ paylinkId }) => ({
        url: `/${paylinkId}/cancel`,
        method: 'PATCH',
        body: {},
      }),
      invalidatesTags: (_result, _error, params) => invalidatePaylinkQueries(params?.paylinkId),
    }),
    getLastestSentPaylinks: builder.query<
      PaylinkResponse[],
      { merchantId: string; pageSize?: number }
    >({
      query: ({ merchantId, pageSize }) => ({
        url: '/',
        params: {
          merchantId,
          pageSize,
          sortBy: 'lastSentAt',
          orderBy: 'desc',
        },
        method: 'GET',
      }),
      transformResponse: (response: PaginatedResponse<PaylinkResponse>) => response.data,
      providesTags: (result) => [
        { type: 'Paylink', id: 'RECENTLY_SENT' },
        ...(result?.map(({ paylinkId }) => ({ type: 'Paylink' as const, id: paylinkId })) ?? []),
      ],
    }),
    updatePaylink: builder.mutation<
      PaylinkResponse,
      { paylinkId: string; body: UpdatePaylinkBody }
    >({
      query: ({ paylinkId, body }) => ({
        url: `/${paylinkId}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_result, _error, params) => invalidatePaylinkQueries(params?.paylinkId),
    }),
    getAllPaylinks: builder.query<PaginatedResponse<PaylinkResponse>, GetAllPaylinksQuery>({
      query: (params) => ({
        url: '/',
        params,
        method: 'GET',
      }),
      serializeQueryArgs: (params) => params.queryArgs,
      providesTags: (result) => [
        { type: 'Paylink', id: 'ALL' },
        ...(result?.data.map(({ paylinkId }) => ({ type: 'Paylink' as const, id: paylinkId })) ??
          []),
      ],
    }),
    attachFile: builder.mutation<PaylinkResponse, { paylinkId: string; file: File }>({
      query: ({ paylinkId, file }) => {
        const formData = new FormData();
        formData.append('file', file);
        return {
          url: `/${paylinkId}/attach`,
          method: 'POST',
          headers: {},
          body: formData,
        };
      },
    }),
    getAttachedFile: builder.query<Blob, { paylinkId: string; attachmentId: string }>({
      query: ({ paylinkId, attachmentId }) => ({
        url: `/${paylinkId}/attachments/${attachmentId}`,
        method: 'GET',
        responseHandler: async (response) => response.blob(),
      }),
      keepUnusedDataFor: ATTACHMENT_VARS.QUERY_CACHE_TTL,
    }),
    deleteFile: builder.mutation<PaylinkResponse, { paylinkId: string; attachmentId: string }>({
      query: ({ paylinkId, attachmentId }) => {
        return {
          url: `/${paylinkId}/attachments/${attachmentId}`,
          method: 'DELETE',
          headers: {},
        };
      },
      invalidatesTags: (_result, _error, params) => invalidatePaylinkQueries(params?.paylinkId),
    }),
    downloadPaylinks: builder.mutation<Record<string, unknown>, DownloadPaylinksBody>({
      query: (body) => ({
        url: '/download',
        method: 'POST',
        body,
      }),
    }),
  }),
});

export const {
  useGetPaylinkQuery,
  useCreatePaylinkMutation,
  useSendPaylinkMutation,
  useGetLastestSentPaylinksQuery,
  useUpdatePaylinkMutation,
  useCancelPaylinkMutation,
  useGetAllPaylinksQuery,
  useLazyGetAllPaylinksQuery,
  useAttachFileMutation,
  useGetAttachedFileQuery,
  useLazyGetAttachedFileQuery,
  useDeleteFileMutation,
  useDownloadPaylinksMutation,
} = paylinkApi;
